import * as ko from 'knockout';

import { EventBus } from "Core/Common/EventBus/EventBus";

import { BackButton } from '../../../Components/BackButton/BackButton';

import { BaseProduct } from "./BaseProduct";

import { RootGroup } from "./RootGroup";
import { PriceList } from "./PriceList";
import { AlternativePriceModel } from 'Core/Components/Controls/ProductConfigurator/Pages/ConfigurationPage/Models/AlternativePriceModel';

import { ProductPart } from './ProductPart';

import { CachedLevel } from './CachedLevel';

import Template from '../Templates/Product.html';
import { CustomFieldControl } from 'Core/Components/Controls/ProductConfigurator/Pages/ConfigurationPage/Models/CustomFields/Controls/CustomFieldControl';
import { ConfigurationPageEvents } from 'Core/Components/Controls/ProductConfigurator/Pages/ConfigurationPage/Events/ConfigurationPageEvents';
import { PropertyControl } from 'Core/Components/Controls/ProductConfigurator/Pages/ConfigurationPage/Models/Properties/Controls/PropertyControl';
import { FIELD_TYPES } from 'Core/Constant';

type EventCallback = (...args: any[]) => void;

export type Property = {
    name: string;
    value: string;
    productName: string;
    productGuid: string;
  };


  export const controlFacade = (properties: Array<Property>, groups: Array<RootGroup>, productParts: Array<{groupGuid: string, product: ProductPart}>, product: Product) => {
    
    const _events: Record<string, EventCallback[]> = {};
    
    const getAllProductProperties = () => {
      return properties;
    };

    const getAllProducts = (): Array<{ name: string; guid: string, groupGuid: string }> => {
        return productParts.map(group => ({ name: group.product.Name, guid: group.product.Guid, groupGuid: group.groupGuid }));
    };
  
    const getAllProductGroups = (): Array<{ name: string; guid: string }> => {
        return groups.map(group => ({ name: group.Name, guid: group.Guid }));
    };

    const addProductGroup = (guid: string) => {
        const group = groups.find(group => group.Guid == guid);
        if (group) {
            group.IsHiddenByApplication(false);
            group.ToggleBody();
        }
    };

    const deleteProductGroup = (guid: string) => {
        const group = groups.find(group => group.Guid == guid);
        if (group) {
            group.IsHiddenByApplication(true);
            group.Remove();
        }
    };

    const on = (eventName: string, callback: EventCallback) => {
      if (!_events[eventName]) {
        _events[eventName] = [];
      }
      _events[eventName].push(callback);
    };
  
    const off = (eventName: string, callback: EventCallback) => {
      if (!_events[eventName]) return;
      _events[eventName] = _events[eventName].filter(cb => cb !== callback);
    };
  
    const emit = (eventName: string, ...args: any[]) => {
      if (!_events[eventName]) return;
      _events[eventName].forEach(cb => cb(...args));
    };

    const setProductProperty = (property: Property) =>{
        product.DispatchEvent(ConfigurationPageEvents.PropertyValueChange, property);
    }

    const makePropertyReadonly = (property: Property) =>{
        product.DispatchEvent(ConfigurationPageEvents.MakePropertyReadonly, property);
    }

    const setImage = (image: string) => {
        product.DispatchEvent(ConfigurationPageEvents.UpdateMainImage, image)
    }

    const setDrawingJson = (json: string) => {
        product.Drawing = json ? JSON.stringify(json) : null;
        product.DispatchEvent(ConfigurationPageEvents.SetDrawing, json)
    }
  
    const getDrawingJson = () => {
        return product.Drawing;
    }

    const addAllProductGroups = () =>{
        groups.forEach(group => {
            group.IsHiddenByApplication(false);
        });
    }

    const deleteProduct = (guid: string) => {
        _.each(groups, group => {
            const product = group.Products().find(product => product.Guid == guid);
            if (product && product.Selected()) {
                product.ToggleSelection();
            }
        });
    }

    return {
        getAllProductProperties,
        getAllProductGroups,
        getAllProducts,
        setProductProperty,
        addAllProductGroups,
        addProductGroup,
        deleteProductGroup,
        deleteProduct,
        makePropertyReadonly,
        setImage,
        setDrawingJson,
        getDrawingJson,
        on,
        off,
        emit,        
    };
  };

export class Product extends BaseProduct {

    private _backButton: BackButton;

    PriceList: KnockoutObservable<PriceList>;

    MainProductPart: ProductPart;

    ViewModels: CachedLevel[];

    ExtensionApplicationUrl: string;

    private _isIframeFullScreen: KnockoutObservable<boolean>;
    private _showFrameExtention: KnockoutObservable<boolean>;
    private _facade: any;

    constructor(id: number,
        kSeq: number,
        name: string,
        nameTranslated: string,
        image: string,
        enableCustomImage: boolean,
        customImageData: string,
        customImageThumbnail: string,
        customImageKSeq: number,
        enableDrawing: boolean,
        drawing: string,
        description: string,
        price: number,
        quantity: number,
        alternativePriceModel: AlternativePriceModel,
        isActiveLevelLocked: boolean,
        extensionApplicationUrl: string,
        isNew: boolean
    ) {
        super(id, kSeq, name, nameTranslated, image, enableCustomImage, enableDrawing, customImageData, customImageThumbnail, customImageKSeq, drawing, description, price, null, isNew);
        
        this.PriceList = ko.observable(new PriceList());
        this.PriceList().AddAlternativePriceModel(alternativePriceModel);
        this.PriceList().AddMainProductPriceGroup(id, name, nameTranslated, price, quantity);
        this.PriceList().IsActiveLevelLocked = isActiveLevelLocked;

        this.ViewModels = [];
        this.ExtensionApplicationUrl = extensionApplicationUrl;
        this._isIframeFullScreen = ko.observable(null);
        this._showFrameExtention = ko.observable(false);
    }

    ChangesFullScreen(): void {
        this._isIframeFullScreen(!this._isIframeFullScreen())
    }

    LevelCacheAvailable() {
        return this.PriceList() &&
            this.PriceList().ActiveLevel != null &&
            this.PriceList().ActiveLevel != this.PriceList().MainLevel;
    }

    AttachBackButton(backButton: BackButton) {
        this._backButton = backButton;
    }

    AssignEventBus(bus: EventBus) {
        super.AssignEventBus(bus);
        this.PriceList().AssignEventBus(bus);
        this.BindEvents();
    }

    BindEvents() {

        this.HandleEvent<ProductPart>(ConfigurationPageEvents.ProductPartSelected)
        .Using(eventArgs => {
            this._facade?.emit('productAdded', { data: {
                guid: eventArgs.Data.Guid, name: eventArgs.Data.Name}
             })
        }).Always();


        this.HandleEvent<ProductPart>(ConfigurationPageEvents.ProductPartUnSelected)
        .Using(eventArgs => {
            this._facade?.emit('productDeleted', { data: {
                guid: eventArgs.Data.Guid, name: eventArgs.Data.Name}
             })
        }).Always();

        this.HandleEvent<any, PropertyControl>(ConfigurationPageEvents.PropertyValueChanged)
            .Using(eventArgs => {
                this._facade?.emit('productPropertyChanged', { data: {
                    name: eventArgs.Source.Name, value: eventArgs.Source.Type == FIELD_TYPES.Lookup ?  eventArgs.Source.GetValueForPreview() : eventArgs.Source.GetValueForSave(), productName: eventArgs.Source.ProductName, productGuid: eventArgs.Source.ProductGuid}
                 })
            })
            .Always();

        this.HandleEvent<any, CustomFieldControl>(ConfigurationPageEvents.CustomFieldValueChanged)
            .Using(eventArgs => {
                const controlValue = eventArgs.Source.GetValueForSave();
                this._facade?.emit('productPropertyChanged', { data: {
                    name: eventArgs.Source.Name, value: controlValue.Text || controlValue.Value, productName: eventArgs.Source.ProductName, productGuid: eventArgs.Source.ProductGuid}
                 })
            })
            .Always();
    }

    InitExtention(
         properties: Array<{ name: string, value: string, productName: string, productGuid: string }>,
         groups: Array<RootGroup>,
         productParts: Array<{groupGuid: string, product: ProductPart}>
        ) {
        this._facade = controlFacade(properties, groups, productParts, this)
        this._showFrameExtention(true);
    }

    FrameLoaded(_, evt) {  
        const event = new CustomEvent('extensionConnected', { detail: { control: this._facade } });
        evt.currentTarget.contentWindow.dispatchEvent(event);
    }

    GetTemplate() {
        return Template;
    }

    RootGroupIs(rootGroup: RootGroup) {
        return false;
    }

    Dispose() {
        this.PriceList().Dispose();
        super.Dispose();
    }
}